import type { LucideIcon } from 'lucide-react';
import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import foodTrackingService, { DailyNutrition } from '../services/foodTrackingService';
import {
  MapPin,
  Calendar,
  Utensils,
  Dumbbell,
  UserCircle,
  ChefHat,
  Activity,
  ChevronRight,
  Target,
  Sparkles,
  MessageCircle,
  BookOpen,
  Users,
  Trophy,
  Pill,
  Brain,
  CheckCircle2,
  Flame,
  Droplets,
  Camera,
  FileText,
  Star,
} from 'lucide-react';

interface MobileDashboardProps {
  stats: {
    profileCompletion: number;
    activeMealPlans: number;
    chatConversations: number;
    trackingLogs: number;
    currentStreak: number;
    activeGoals: number;
    foodEntriesToday: number;
    aiPatterns: number;
  };
}

export default function MobileDashboard({ stats }: MobileDashboardProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  // FII FIT = aplicație 100% fitness. Modul CHRONIC este complet dezactivat
  // la nivel UI. Forțăm `false` pentru a ascunde toate cardurile/CTA-urile
  // medicale care altfel ar fi randate condițional în acest dashboard.
  const isChronicMode = false;
  void user; // user este folosit în altă parte; păstrăm referința

  // Calorie data from food tracking
  const [calorieData, setCalorieData] = useState<DailyNutrition | null>(null);
  const [calorieLoading, setCalorieLoading] = useState(true);

  // Get local date string
  const getLocalDateString = (d = new Date()) => {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  // Fetch calorie data
  useEffect(() => {
    const fetchCalories = async () => {
      try {
        const today = getLocalDateString();
        const data = await foodTrackingService.getDailyNutrition(today);
        setCalorieData(data);
      } catch (err) {
        console.log('[MobileDashboard] No calorie data available');
        // Set default data
        setCalorieData({
          date: getLocalDateString(),
          entries: [],
          totals: { calories: 0, protein: 0, carbs: 0, fat: 0, quantity: 0 },
          remaining: { calories: 2000, protein: 150, carbs: 250, fat: 65 },
          byMealType: {
            breakfast: { calories: 0, protein: 0, carbs: 0, fat: 0 },
            lunch: { calories: 0, protein: 0, carbs: 0, fat: 0 },
            dinner: { calories: 0, protein: 0, carbs: 0, fat: 0 },
            snack: { calories: 0, protein: 0, carbs: 0, fat: 0 },
          },
        });
      } finally {
        setCalorieLoading(false);
      }
    };
    fetchCalories();
  }, []);

  // Calorie calculations
  const calorieGoal = calorieData?.goals?.targetCalories || 2000;
  const calorieConsumed = calorieData?.totals?.calories || 0;
  const calorieRemaining = calorieData?.remaining?.calories || calorieGoal;
  const caloriePercentage = calorieGoal > 0 ? Math.min((calorieConsumed / calorieGoal) * 100, 100) : 0;
  const isOverGoal = calorieConsumed > calorieGoal;

  // Macro data
  const proteinConsumed = calorieData?.totals?.protein || 0;
  const proteinGoal = calorieData?.goals?.targetProtein || 150;
  const carbsConsumed = calorieData?.totals?.carbs || 0;
  const carbsGoal = calorieData?.goals?.targetCarbs || 250;
  const fatConsumed = calorieData?.totals?.fat || 0;
  const fatGoal = calorieData?.goals?.targetFats || 65;

  // Format date
  const formatDate = () => {
    return new Intl.DateTimeFormat('ro-RO', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    }).format(new Date());
  };

  // Shortcut cards for CHRONIC mode
  const chronicShortcuts = [
    {
      icon: MapPin,
      iconBg: 'bg-red-50',
      iconColor: 'text-red-500',
      title: 'Specialiști',
      subtitle: 'Hartă interactivă',
      path: '/specialists',
    },
    {
      icon: Calendar,
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-500',
      title: 'Calendar',
      subtitle: `${stats.trackingLogs} log-uri`,
      path: '/calendar',
      badge: stats.trackingLogs > 0 ? stats.trackingLogs.toString() : undefined,
    },
    {
      icon: Utensils,
      iconBg: 'bg-amber-50',
      iconColor: 'text-amber-500',
      title: 'Mese AI',
      subtitle: `${stats.foodEntriesToday} alimente azi`,
      path: '/food-tracking',
    },
    {
      icon: Activity,
      iconBg: 'bg-purple-50',
      iconColor: 'text-purple-500',
      title: 'Activitate',
      subtitle: '0 exerciții',
      path: '/advanced-tracking',
    },
  ];

  const chronicBottomRow = [
    {
      icon: UserCircle,
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-500',
      title: 'Profilul\nMeu',
      subtitle: `${stats.profileCompletion}%\ncompletat`,
      path: '/profile',
      badge: `${stats.profileCompletion}%`,
      badgeColor: stats.profileCompletion === 100 ? 'bg-green-500' : 'bg-orange-500',
    },
    {
      icon: ChefHat,
      iconBg: 'bg-green-50',
      iconColor: 'text-green-500',
      title: 'Nutriție',
      subtitle: `${stats.activeMealPlans} plan activ`,
      path: '/nutrition',
      badge: stats.activeMealPlans > 0 ? stats.activeMealPlans.toString() : undefined,
      badgeColor: 'bg-green-500',
    },
    {
      icon: Dumbbell,
      iconBg: 'bg-purple-50',
      iconColor: 'text-purple-500',
      title: 'Exerciții',
      subtitle: 'Video adaptate',
      path: '/exercises',
      badge: '100+',
      badgeColor: 'bg-purple-500',
    },
  ];


  // Shortcut cards for HEALTHY mode
  const healthyShortcuts: Array<{ icon: LucideIcon; iconBg: string; iconColor: string; title: string; subtitle: string; path: string; badge?: string }> = [
    {
      icon: MapPin,
      iconBg: 'bg-red-50',
      iconColor: 'text-red-500',
      title: 'Specialiști',
      subtitle: 'Hartă interactivă',
      path: '/specialists',
    },
    {
      icon: Utensils,
      iconBg: 'bg-amber-50',
      iconColor: 'text-amber-500',
      title: 'Mese AI',
      subtitle: `${stats.foodEntriesToday} alimente azi`,
      path: '/food-tracking',
    },
    {
      icon: Activity,
      iconBg: 'bg-purple-50',
      iconColor: 'text-purple-500',
      title: 'Activitate',
      subtitle: '0 exerciții',
      path: '/exercises',
    },
  ];

  const healthyBottomRow = [
    {
      icon: UserCircle,
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-500',
      title: 'Profilul\nMeu',
      subtitle: `${stats.profileCompletion}%\ncompletat`,
      path: '/profile',
      badge: `${stats.profileCompletion}%`,
      badgeColor: stats.profileCompletion === 100 ? 'bg-green-500' : 'bg-orange-500',
    },
    {
      icon: ChefHat,
      iconBg: 'bg-green-50',
      iconColor: 'text-green-500',
      title: 'Nutriție',
      subtitle: `${stats.activeMealPlans} plan activ`,
      path: '/nutrition',
      badge: stats.activeMealPlans > 0 ? stats.activeMealPlans.toString() : undefined,
      badgeColor: 'bg-green-500',
    },
    {
      icon: Dumbbell,
      iconBg: 'bg-purple-50',
      iconColor: 'text-purple-500',
      title: 'Exerciții',
      subtitle: 'Video adaptate',
      path: '/exercises',
      badge: '100+',
      badgeColor: 'bg-purple-500',
    },
  ];

  const healthyExtraShortcuts = [
    {
      icon: Camera,
      iconBg: 'bg-orange-50',
      iconColor: 'text-orange-500',
      title: 'Foto Progres',
      path: '/progress-photos',
    },
    {
      icon: Target,
      iconBg: 'bg-orange-50',
      iconColor: 'text-orange-500',
      title: 'Obiective',
      path: '/goals',
    },
    {
      icon: Trophy,
      iconBg: 'bg-yellow-50',
      iconColor: 'text-yellow-600',
      title: 'Provocari',
      path: '/challenges',
    },
    {
      icon: Sparkles,
      iconBg: 'bg-cyan-50',
      iconColor: 'text-cyan-500',
      title: 'AI Tracking',
      path: '/insights',
    },
    {
      icon: Droplets,
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-500',
      title: 'Aport Apa',
      path: '/water-intake',
    },
    {
      icon: BookOpen,
      iconBg: 'bg-teal-50',
      iconColor: 'text-teal-500',
      title: 'Educatie',
      path: '/education',
    },
    {
      icon: FileText,
      iconBg: 'bg-amber-50',
      iconColor: 'text-amber-600',
      title: 'Jurnal',
      path: '/journal',
    },
    {
      icon: Star,
      iconBg: 'bg-yellow-50',
      iconColor: 'text-yellow-600',
      title: 'Recompense',
      path: '/rewards',
    },
    {
      icon: Users,
      iconBg: 'bg-indigo-50',
      iconColor: 'text-indigo-500',
      title: 'Invita Prieteni',
      path: '/referrals',
    },
    {
      icon: Flame,
      iconBg: 'bg-orange-50',
      iconColor: 'text-orange-500',
      title: 'Retete',
      path: '/food-community',
    },
  ];

  // CHRONIC extra shortcuts - include therapy-chat, advanced-tracking, community (CHRONIC only)
  const chronicExtraShortcutsUpdated = [
    {
      icon: Pill,
      iconBg: 'bg-cyan-50',
      iconColor: 'text-cyan-600',
      title: 'Medicamente',
      path: '/profile/medications',
    },
    {
      icon: MessageCircle,
      iconBg: 'bg-indigo-50',
      iconColor: 'text-indigo-500',
      title: 'Chat Terapeutic',
      path: '/therapy-chat',
    },
    {
      icon: Activity,
      iconBg: 'bg-pink-50',
      iconColor: 'text-pink-500',
      title: 'Tracking Avansat',
      path: '/advanced-tracking',
    },
    {
      icon: Users,
      iconBg: 'bg-violet-50',
      iconColor: 'text-violet-500',
      title: 'Comunitate',
      path: '/community',
    },
    {
      icon: BookOpen,
      iconBg: 'bg-teal-50',
      iconColor: 'text-teal-500',
      title: 'Educație',
      path: '/education',
    },
  ];

  const shortcuts = isChronicMode ? chronicShortcuts : healthyShortcuts;
  const bottomRow = isChronicMode ? chronicBottomRow : healthyBottomRow;
  const extraShortcuts = isChronicMode ? chronicExtraShortcutsUpdated : healthyExtraShortcuts;

  // Personalized plan content
  const getPersonalizedPlan = () => {
    const healthGoal = user?.healthGoal;
    if (!healthGoal) return null;

    const plans: Record<string, { emoji: string; title: string; tip: string }> = {
      // HEALTHY
      WEIGHT_LOSS: { emoji: '🔥', title: 'Slăbire sănătoasă', tip: 'Deficit caloric 300-500 kcal/zi, hidratare 2-3L' },
      MUSCLE_GAIN: { emoji: '💪', title: 'Masă musculară', tip: 'Surplus +300-500 kcal/zi, proteine 1.6-2g/kg' },
      HEALTHY_LIFESTYLE: { emoji: '🥗', title: 'Stil de viață sănătos', tip: '50% carbo, 30% proteine, 20% grăsimi' },
      REDUCE_BELLY_FAT: { emoji: '🔥', title: 'Reducere grăsime abdominală', tip: 'HIIT 20-30 min, low-carb, fără zahăr' },
      INCREASE_ENERGY: { emoji: '⚡', title: 'Energie maximă', tip: 'Dietă antiinflamatoare, somn 7-9h' },
      CLEAN_EATING: { emoji: '🌿', title: 'Alimentație curată', tip: 'Alimente neprocesate, max 5 ingrediente' },
      REDUCE_SUGAR_CARBS: { emoji: '🥒', title: 'Low carb', tip: '<100g carbo/zi, focus pe proteine & grăsimi' },
      BETTER_SLEEP: { emoji: '😴', title: 'Somn calitativ', tip: 'Rutină seară, fără ecrane 1h înainte' },
      MAINTAIN_WEIGHT: { emoji: '⚖️', title: 'Menținere greutate', tip: 'Consum = Ardere, consistență zilnică' },
      START_EXERCISE: { emoji: '🚶', title: 'Început exerciții', tip: 'Start cu 10-15 min/zi, creștere graduală' },
      IMPROVE_FOCUS: { emoji: '🧠', title: 'Focus mental', tip: 'Omega-3, meditație 5-10 min zilnic' },
      REDUCE_BLOATING: { emoji: '🥗', title: 'Reducere balonare', tip: 'Dietă FODMAP, jurnal alimentație' },
      // CHRONIC
      EASY_STANDING: { emoji: '💪', title: 'Ridicare ușoară', tip: 'Exerciții picioare 10-15 min zilnic' },
      REDUCE_INFLAMMATION: { emoji: '🥗', title: 'Reducere inflamație', tip: 'Omega-3, curcuma, fără zahăr' },
      RECOVER_MUSCLE_STRENGTH: { emoji: '🏋️', title: 'Recuperare musculară', tip: 'Greutate proprie, kinetoterapie video' },
      CLIMB_STAIRS: { emoji: '🫁', title: 'Urcat scări', tip: 'Respirație controlată, mobilitate articulară' },
      REDUCE_JOINT_PAIN: { emoji: '🧘', title: 'Reducere dureri articulare', tip: 'Pește gras, yoga, stretching' },
      BREATHE_BETTER: { emoji: '🫁', title: 'Respirație mai bună', tip: 'Respirație diafragmală, plan anti-mucus' },
      STABILIZE_GLUCOSE: { emoji: '🩸', title: 'Stabilizare glicemie', tip: 'Low GI, fibre, porții mici frecvente' },
      IMPROVE_BALANCE: { emoji: '🧘', title: 'Echilibru mai bun', tip: 'Tai Chi, yoga, exerciții stabilitate' },
      REDUCE_CHRONIC_FATIGUE: { emoji: '😴', title: 'Reducere oboseală', tip: 'Somn 8-9h, fier, B12, magneziu' },
      IMPROVE_DIGESTION: { emoji: '🥗', title: 'Digestie mai bună', tip: 'Plan FODMAP, fără iritanți' },
      REGAIN_MOBILITY: { emoji: '🤲', title: 'Recuperare mobilitate', tip: 'ROM exercises 10-15 min zilnic' },
      PREVENT_DISEASE_WORSENING: { emoji: '🥗', title: 'Prevenție agravare', tip: 'Plan nutrițional specific bolii' },
      HEALTHY_WEIGHT_LOSS_CHRONIC: { emoji: '🐌', title: 'Slăbit adaptat', tip: 'Ritm sănătos: 0.25-0.5 kg/săpt, fără foame extremă' },
      CONTROL_CHRONIC_PAIN: { emoji: '📝', title: 'Control durere cronică', tip: 'Jurnal durere, yoga, meditație' },
      IMPROVE_LIFE_WITH_DISEASE: { emoji: '🎯', title: 'Viață mai bună cu boala', tip: 'AI personalizat, educație, tracking complet' },
    };

    return plans[healthGoal] || null;
  };

  const personalizedPlan = useMemo(() => getPersonalizedPlan(), [user?.healthGoal]);

  return (
    <div className="px-4 pt-4 pb-24 space-y-4">
      {/* Compact Welcome */}
      <div>
        <h1 className="text-xl font-bold text-gray-900">
          Bună, {user?.firstName || user?.email?.split('@')[0]}! 👋
        </h1>
        <p className="text-sm text-gray-500 capitalize">{formatDate()}</p>
      </div>

      {/* Calorie Dashboard Card */}
      <div
        className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100"
        onClick={() => navigate('/food-tracking')}
      >
        <h3 className="text-base font-semibold text-gray-900 mb-4">Dashboard Calorii</h3>

        {calorieLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-5">
              {/* Circular Progress */}
              <div className="w-24 h-24 flex-shrink-0">
                <CircularProgressbar
                  value={caloriePercentage}
                  maxValue={100}
                  styles={buildStyles({
                    pathColor: isOverGoal ? '#ef4444' : '#8b5cf6',
                    trailColor: '#f3f4f6',
                    strokeLinecap: 'round',
                  })}
                />
              </div>

              {/* Calorie Info */}
              <div className="flex-1">
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-gray-900">
                    {calorieRemaining > 0 ? calorieRemaining : 0}
                  </span>
                  <span className="text-sm text-gray-500">kcal rămase</span>
                </div>
                <p className="text-xs text-gray-400 mb-3">/ Obiectiv {calorieGoal}</p>

                <div className="flex items-center gap-6 text-xs">
                  <div className="flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                    <span className="text-gray-600">Consumat</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Flame className="w-3.5 h-3.5 text-purple-500" />
                    <span className="text-gray-600">Activități</span>
                  </div>
                </div>
                <div className="flex items-center gap-6 text-xs mt-0.5">
                  <div className="flex items-center gap-1.5">
                    <Utensils className="w-3.5 h-3.5 text-amber-500" />
                    <span className="font-semibold text-gray-900">{calorieConsumed} kcal</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Activity className="w-3.5 h-3.5 text-purple-400" />
                    <span className="font-semibold text-gray-900">kcal</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Macro Bars */}
            <div className="flex gap-3 mt-5">
              <MacroMiniBar
                label="Proteine"
                consumed={proteinConsumed}
                goal={proteinGoal}
                color="bg-blue-500"
              />
              <MacroMiniBar
                label="Carbohidrați"
                consumed={carbsConsumed}
                goal={carbsGoal}
                color="bg-yellow-500"
              />
              <MacroMiniBar
                label="Grăsimi"
                consumed={fatConsumed}
                goal={fatGoal}
                color="bg-orange-500"
              />
            </div>
          </>
        )}
      </div>

      {/* Main Shortcuts Grid (2x2) */}
      <div className="grid grid-cols-2 gap-3">
        {shortcuts.map((item, idx) => (
          <button
            key={idx}
            onClick={() => navigate(item.path)}
            className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 text-left flex flex-col gap-2 active:scale-[0.97] transition-transform"
          >
            <div className="flex items-center justify-between w-full">
              <div className={`w-10 h-10 ${item.iconBg} rounded-xl flex items-center justify-center`}>
                <item.icon className={`w-5 h-5 ${item.iconColor}`} />
              </div>
              {item.badge && (
                <span className="bg-blue-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                  {item.badge}
                </span>
              )}
              <ChevronRight className="w-4 h-4 text-gray-300" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">{item.title}</p>
              <p className="text-[11px] text-gray-400">{item.subtitle}</p>
            </div>
          </button>
        ))}
      </div>

      {/* Bottom Row - 3 items with badges */}
      <div className="grid grid-cols-3 gap-2.5">
        {bottomRow.map((item, idx) => (
          <button
            key={idx}
            onClick={() => navigate(item.path)}
            className="bg-white rounded-2xl p-3 shadow-sm border border-gray-100 text-center flex flex-col items-center gap-1.5 active:scale-[0.97] transition-transform relative"
          >
            {item.badge && (
              <span className={`absolute -top-1.5 -right-1.5 ${item.badgeColor} text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full border-2 border-white shadow-sm`}>
                {item.badge}
              </span>
            )}
            <div className={`w-9 h-9 ${item.iconBg} rounded-xl flex items-center justify-center`}>
              <item.icon className={`w-4.5 h-4.5 ${item.iconColor}`} />
            </div>
            <p className="text-xs font-semibold text-gray-900 leading-tight whitespace-pre-line">
              {item.title}
            </p>
            <p className="text-[10px] text-gray-400 leading-tight whitespace-pre-line">
              {item.subtitle}
            </p>
          </button>
        ))}
      </div>

      {/* Extra Quick Access - 3 col grid */}
      <div className="grid grid-cols-3 gap-2.5">
        {extraShortcuts.map((item, idx) => (
          <button
            key={idx}
            onClick={() => navigate(item.path)}
            className="bg-white rounded-xl p-3 shadow-sm border border-gray-100 flex flex-col items-center gap-1.5 active:scale-[0.97] transition-transform"
          >
            <div className={`w-8 h-8 ${item.iconBg} rounded-lg flex items-center justify-center`}>
              <item.icon className={`w-4 h-4 ${item.iconColor}`} />
            </div>
            <p className="text-[11px] font-medium text-gray-700 text-center leading-tight">
              {item.title}
            </p>
          </button>
        ))}
      </div>

      {/* Personalized Plan Banner */}
      {personalizedPlan && (
        <div
          className="bg-gradient-to-r from-purple-600 via-blue-600 to-indigo-600 rounded-2xl p-4 text-white shadow-lg"
          onClick={() => navigate('/health-goal')}
        >
          <h3 className="text-sm font-bold mb-2">
            Plan personalizat pentru obiectivul tău
          </h3>
          <div className="bg-white/10 rounded-xl p-3 backdrop-blur-sm">
            <div className="flex items-start gap-2">
              <span className="text-lg">{personalizedPlan.emoji}</span>
              <div>
                <p className="text-sm font-semibold">{personalizedPlan.title}</p>
                <p className="text-xs text-white/80 mt-0.5">{personalizedPlan.tip}</p>
              </div>
            </div>
          </div>
          {isChronicMode && (
            <div className="bg-white/10 rounded-xl p-3 backdrop-blur-sm mt-2">
              <div className="flex items-start gap-2">
                <span className="text-lg">🥗</span>
                <div>
                  <p className="text-sm font-semibold">Dietă adaptată bolii</p>
                  <p className="text-xs text-white/80 mt-0.5">Rețete și planuri nutriționale personalizate</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Rewards & Referrals CTA */}
      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={() => navigate('/rewards')}
          className="bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl p-4 text-white shadow-md flex flex-col gap-2 active:scale-[0.98] transition-transform"
        >
          <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center">
            <Star className="w-5 h-5 text-white" />
          </div>
          <div className="text-left">
            <p className="text-sm font-bold">Recompense</p>
            <p className="text-xs text-white/80">Castiga credite</p>
          </div>
        </button>
        <button
          onClick={() => navigate('/referrals')}
          className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl p-4 text-white shadow-md flex flex-col gap-2 active:scale-[0.98] transition-transform"
        >
          <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center">
            <Users className="w-5 h-5 text-white" />
          </div>
          <div className="text-left">
            <p className="text-sm font-bold">Invita Prieteni</p>
            <p className="text-xs text-white/80">Pana la 150 credite</p>
          </div>
        </button>
      </div>

      {/* Chat AI Quick Access */}
      <button
        onClick={() => navigate('/chat')}
        className="w-full bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl p-4 text-white shadow-md flex items-center justify-between active:scale-[0.98] transition-transform"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
            <Brain className="w-5 h-5" />
          </div>
          <div className="text-left">
            <p className="text-sm font-semibold">Intreaba AI-ul Fitness</p>
            <p className="text-xs text-white/70">Asistent 24/7 pentru fitness-ul tau</p>
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-white/60" />
      </button>
    </div>
  );
}

// Mini macro bar component
function MacroMiniBar({
  label,
  consumed,
  goal,
  color,
}: {
  label: string;
  consumed: number;
  goal: number;
  color: string;
}) {
  const percentage = goal > 0 ? Math.min((consumed / goal) * 100, 100) : 0;

  return (
    <div className="flex-1">
      <p className="text-[11px] font-medium text-gray-600 mb-1">{label}</p>
      <div className="w-full bg-gray-100 rounded-full h-1.5">
        <div
          className={`${color} h-1.5 rounded-full transition-all duration-300`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <p className="text-[10px] text-gray-400 mt-0.5">
        {Math.round(consumed)}/{goal}g
      </p>
    </div>
  );
}
